import { Injectable, signal, effect, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { ApiUtils } from '../../../shared/utils/api.utils';
import { Language } from '../../models/language.model';
import { ApiResponse } from '../../models/api.model';
import { CookieService } from './cookie.service';
import { UserInfoKey } from '@/app/shared/constants';
import { AuthUser } from '@/app/pages/auth/models/auth.model';
import { EnvService } from './env.service';
import { NzI18nInterface } from 'ng-zorro-antd/i18n';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly envService = inject(EnvService);
  private readonly httpClient = inject(HttpClient);
  private readonly cookieService = inject(CookieService);
  private readonly translations = signal<Record<string, unknown>>({});
  private readonly currentLang = signal<string>('VI');
  private isInitialized = false;
  public languageList = signal<Language[]>([]);
  public userInfo = signal<AuthUser | null>(null);

  /**
   * Mapping language codes to ng-zorro locales and Angular locale data
   * This allows easy extension for multiple languages
   */
  private static readonly LOCALE_MAP: Record<
    string,
    {
      nzLocale: () => Promise<NzI18nInterface>;
      angularLocale: () => Promise<any>;
    }
  > = {
    VI: {
      nzLocale: async () => (await import('ng-zorro-antd/i18n')).vi_VN,
      angularLocale: async () => (await import('@angular/common/locales/vi')).default,
    },
    EN: {
      nzLocale: async () => (await import('ng-zorro-antd/i18n')).en_US,
      angularLocale: async () => (await import('@angular/common/locales/en')).default,
    },
    // Easy to add more languages:
    // 'FR': {
    //   nzLocale: async () => (await import('ng-zorro-antd/i18n')).fr_FR,
    //   angularLocale: async () => (await import('@angular/common/locales/fr')).default,
    // },
    // 'JA': {
    //   nzLocale: async () => (await import('ng-zorro-antd/i18n')).ja_JP,
    //   angularLocale: async () => (await import('@angular/common/locales/ja')).default,
    // },
  };

  constructor() {
    const userInfo = this.cookieService.getCookie(UserInfoKey)
      ? JSON.parse(this.cookieService.getCookie(UserInfoKey) as string)
      : null;
    this.userInfo.set(userInfo as AuthUser);

    effect(() => {
      const lang = this.currentLang();
      ApiUtils.setToStorage('app_language', lang);
    });
  }

  /**
   * Get ng-zorro locale for the given language code
   */
  static async getNzLocale(langCode: string): Promise<NzI18nInterface> {
    const locale = this.LOCALE_MAP[langCode] || this.LOCALE_MAP['VI'];
    return await locale.nzLocale();
  }

  /**
   * Get Angular locale data for the given language code
   */
  static async getAngularLocale(langCode: string): Promise<any> {
    const locale = this.LOCALE_MAP[langCode] || this.LOCALE_MAP['VI'];
    return await locale.angularLocale();
  }

  /**
   * Get supported language codes
   */
  static getSupportedLanguages(): string[] {
    return Object.keys(this.LOCALE_MAP);
  }

  /**
   * Check if a language code is supported
   */
  static isLanguageSupported(langCode: string): boolean {
    return langCode in this.LOCALE_MAP;
  }

  initLanguage(): Observable<unknown> {
    if (this.isInitialized) return of(this.translations());

    const savedLang = this.getSavedLanguage();
    this.currentLang.set(savedLang);
    
    // Chỉ load language list nếu user đã đăng nhập và chưa load
    const hasAuth = this.userInfo() !== null;
    if (hasAuth && this.languageList().length === 0) {
      this.loadLanguageList(savedLang);
    }

    return this.loadTranslations(savedLang).pipe(tap(() => (this.isInitialized = true)));
  }

  // Load language list data
  loadLanguageList(savedLang: string): void {
    // Lấy API URL động để đảm bảo config đã load
    const apiUrl = this.envService.getApiUrl();
    
    this.httpClient
      .post<ApiResponse<Language[]>>(`${apiUrl}/api/business/private`, {
        BizType: 'SYS_LANGUAGE_LIST',
        JsonHeader: {
          lang: savedLang || 'VI',
          user: this.userInfo()?.LoginName ?? '',
          channel: 'BACK',
          branchCode: this.userInfo()?.branchCode ?? '',
          branchId: this.userInfo()?.branchId ?? 0,
        },
        JsonParam: {},
      })
      .pipe(
        map((res) => {
          if (res.Data && res.Data.length > 0 && res.Rc === 0) {
            this.languageList.set(res.Data as Language[]);
          } else {
            this.languageList.set([]);
          }
        }),
        catchError((error) => {
          console.error('Error getting language:', error);
          this.languageList.set([]);
          return of([]);
        }),
      )
      .subscribe();
  }

  getCurrentLang(): string {
    return this.currentLang();
  }

  setLang(lang: string): Observable<boolean> {
    this.currentLang.set(lang);
    return of(true);
  }

  /**
   * Gọi sau khi login thành công để cập nhật userInfo và load language list
   */
  onLoginSuccess(): void {
    const userInfo = this.cookieService.getCookie(UserInfoKey)
      ? JSON.parse(this.cookieService.getCookie(UserInfoKey) as string)
      : null;
    this.userInfo.set(userInfo as AuthUser);

    // Chỉ load nếu chưa có data
    if (userInfo && this.languageList().length === 0) {
      this.loadLanguageList(this.currentLang());
    }
  }

  private getSavedLanguage(): string {
    const stored = ApiUtils.getFromStorage('app_language') as string;
    // Validate against supported languages
    return I18nService.isLanguageSupported(stored) ? stored : 'VI';
  }

  private loadTranslations(lang: string): Observable<unknown> {
    return this.httpClient
      .get<Record<string, unknown>>(`/assets/i18n/${lang.toLowerCase()}.json`)
      .pipe(
        tap((translations) => this.translations.set(translations)),
        catchError((error) => {
          console.error(`Failed to load translations for ${lang}:`, error);
          return of({});
        }),
      );
  }

  translate(key: string, params?: Record<string, unknown>): string {
    const value = ApiUtils.getNestedValue(this.translations(), key);

    if (typeof value !== 'string') return key;

    return params
      ? Object.entries(params).reduce(
          (text, [k, v]) => text.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), String(v)),
          value,
        )
      : value;
  }
}
